import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dropdown } from 'react-bootstrap';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { FaChevronDown, FaChevronRight, FaPlus } from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import {
  getEnabledPackages,
  getAllowedTabNames,
  getAllowedTabIds,
  hasPackage,
  isWatconnectNavTab,
} from '../../utils/entitlements';
import { theme } from '../../theme/theme';
import './css/Menu.css';

const titleCase = (str) =>
  (str || '').replace(/\w\S*/g, (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase());

/** Dev: always on in development. Prod: set localStorage spark_debug_tabs=1 */
const tabMenuDebugEnabled = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage?.getItem('spark_debug_tabs') === '1') {
      return true;
    }
  } catch (e) {
    /* ignore */
  }
  return process.env.NODE_ENV === 'development';
};

/**
 * Single place for tab list + bar items; exposes menuDebug when tabs disappear after login.
 */
function buildMenuTabComputation({
  activeTabs,
  selectedApp,
  enabledPackages,
  allowedTabIds,
  allowedTabNames,
  hasWatconnectPackage,
}) {
  const getGroupKey = (t) => (t.appGroupKey ?? t.config?.group_key ?? t.group_key)?.trim();
  const getGroupLabel = (t) => (t.appGroupLabel ?? t.config?.group_label ?? t.group_label)?.trim();
  const getTabPackage = (t) => (t.config?.package ?? t.package)?.trim?.() || '';

  const normalizeTabRef = (s) => (s || '').toString().toLowerCase().trim();
  const allowedIdSet = allowedTabIds.length > 0 ? new Set(allowedTabIds.map((id) => String(id))) : null;
  const allowedNameSet = allowedTabNames.length > 0 ? new Set(allowedTabNames) : null;
  const normalizeName = (s) => {
    const x = (s || '').toString().toLowerCase().trim();
    return x === 'calender' ? 'calendar' : x;
  };

  const hideUserMailTemplate = (t) => {
    const obj = normalizeTabRef(t?.object_name);
    const name = normalizeTabRef(t?.name);
    if (obj === 'user' || name === 'user') return false;
    if (obj === 'mail_config' || name === 'mail_config' || name === 'mailconfig') return false;
    if (obj === 'email_template' || name === 'email_template' || name === 'emailtemplate') return false;
    return true;
  };

  const trace = [];
  let unresolvedAppEntries = [];
  let visibleTabs;

  const hasAppTabs =
    selectedApp?.config &&
    (selectedApp.config.tabs?.length > 0 || selectedApp.config.tabIds?.length > 0);

  if (hasAppTabs) {
    const appTabsConfig = selectedApp.config.tabs?.length
      ? selectedApp.config.tabs
      : (selectedApp.config.tabIds || []).map((id) => ({ id }));
    const tabById = new Map(activeTabs.map((t) => [String(t.id), t]));
    const tabByName = new Map(activeTabs.map((t) => [(t?.name || '').toString().toLowerCase().trim(), t]));

    visibleTabs = appTabsConfig
      .map((entry) => {
        const tab =
          (entry.id != null ? tabById.get(String(entry.id)) : null) ||
          (entry.name != null
            ? tabByName.get((entry.name || '').toString().toLowerCase().trim())
            : null);
        if (!tab) {
          unresolvedAppEntries.push({
            entryId: entry.id,
            entryName: entry.name,
            entryGroup: entry.group_key,
          });
          return null;
        }
        return {
          ...tab,
          appGroupKey: entry.group_key?.trim() || undefined,
          appGroupLabel: entry.group_label?.trim() || undefined,
        };
      })
      .filter(Boolean);
    trace.push({
      step: 'after_app_config_merge',
      count: visibleTabs.length,
      unresolvedAppConfigEntries: unresolvedAppEntries.length,
    });
  } else {
    visibleTabs = activeTabs.filter((t) => {
      const pkg = getTabPackage(t);
      return !pkg || enabledPackages.includes(pkg);
    });
    trace.push({ step: 'after_default_package_filter', count: visibleTabs.length });
    visibleTabs = visibleTabs.filter(hideUserMailTemplate);
    trace.push({ step: 'after_default_hidden_tabs', count: visibleTabs.length });
  }

  visibleTabs = visibleTabs.filter(hideUserMailTemplate);
  trace.push({ step: 'after_common_hidden_tabs', count: visibleTabs.length });

  if (hasAppTabs) {
    const beforePkg = visibleTabs.length;
    visibleTabs = visibleTabs.filter((t) => {
      const pkg = getTabPackage(t);
      return !pkg || enabledPackages.includes(pkg);
    });
    trace.push({
      step: 'after_app_package_filter',
      count: visibleTabs.length,
      dropped: beforePkg - visibleTabs.length,
    });
  }

  let licenseDropDetail = null;
  const beforeLicense = visibleTabs;

  if (allowedIdSet && allowedIdSet.size > 0) {
    const rejects = beforeLicense.filter((t) => {
      if (t?.is_standard === false) return false;
      if (!t?.standard_tab_id) return true;
      return !allowedIdSet.has(String(t.standard_tab_id));
    });
    licenseDropDetail = {
      mode: 'allowedTabIds',
      allowedIdCount: allowedIdSet.size,
      rejectedCount: rejects.length,
      rejectedSample: rejects.slice(0, 20).map((t) => ({
        id: t.id,
        name: t.name,
        is_standard: t.is_standard,
        standard_tab_id: t.standard_tab_id,
        reason: !t?.standard_tab_id ? 'missing_standard_tab_id' : 'standard_tab_id_not_in_license',
      })),
    };
    visibleTabs = visibleTabs.filter((t) => {
      if (t?.is_standard === false) return true;
      return t?.standard_tab_id && allowedIdSet.has(String(t.standard_tab_id));
    });
  } else if (allowedNameSet && allowedNameSet.size > 0) {
    const rejects = beforeLicense.filter((t) => {
      if (t?.is_standard === false) return false;
      const name = normalizeName(t?.name);
      const label = normalizeName(t?.label);
      const obj = normalizeName(t?.object_name);
      const ok =
        (name && allowedNameSet.has(name)) ||
        (label && allowedNameSet.has(label)) ||
        (obj && allowedNameSet.has(obj));
      return !ok;
    });
    licenseDropDetail = {
      mode: 'allowedTabNames',
      allowedNameCount: allowedNameSet.size,
      rejectedCount: rejects.length,
      rejectedSample: rejects.slice(0, 20).map((t) => ({
        id: t.id,
        name: t.name,
        label: t.label,
        object_name: t.object_name,
        is_standard: t.is_standard,
      })),
    };
    visibleTabs = visibleTabs.filter((t) => {
      if (t?.is_standard === false) return true;
      const name = normalizeName(t?.name);
      const label = normalizeName(t?.label);
      const obj = normalizeName(t?.object_name);
      return (
        (name && allowedNameSet.has(name)) ||
        (label && allowedNameSet.has(label)) ||
        (obj && allowedNameSet.has(obj))
      );
    });
  } else {
    trace.push({
      step: 'license_filter_skipped',
      note: 'No allowedTabIds / allowedTabNames in session entitlements',
    });
  }
  trace.push({ step: 'after_license_filter', count: visibleTabs.length, licenseDropDetail });

  const beforeWatconnect = visibleTabs.length;
  if (!hasWatconnectPackage) {
    visibleTabs = visibleTabs.filter((t) => !isWatconnectNavTab(t));
  }
  trace.push({
    step: 'after_watconnect_filter',
    count: visibleTabs.length,
    droppedWatconnectOnlyTabs: hasWatconnectPackage ? 0 : beforeWatconnect - visibleTabs.length,
  });

  const slotOrder = selectedApp?.config?.slotOrder;
  let barItems;
  if (Array.isArray(slotOrder) && slotOrder.length > 0 && selectedApp?.config?.tabs?.length > 0) {
    barItems = [];
    slotOrder.forEach((slot) => {
      if (slot?.type === 'tab') {
        const tab = visibleTabs.find(
          (t) =>
            (slot.name != null &&
              (t?.name || '').toString().toLowerCase().trim() ===
                (slot.name || '').toString().toLowerCase().trim()) ||
            (slot.id != null && String(t?.id) === String(slot.id))
        );
        if (tab) barItems.push({ type: 'tab', tab });
      } else if (slot?.type === 'group' && slot.group_key) {
        const gk = (slot.group_key || '').trim();
        const groupTabs = visibleTabs
          .filter((t) => getGroupKey(t) === gk)
          .sort((a, b) => (Number(a.order_no) ?? 0) - (Number(b.order_no) ?? 0));
        if (groupTabs.length > 0) {
          const label = getGroupLabel(groupTabs[0]) || titleCase(gk);
          barItems.push({ type: 'group', id: `api-${gk}`, key: gk, label, tabs: groupTabs });
        }
      }
    });
  } else {
    const tabsByOrder = [...visibleTabs].sort(
      (a, b) => (Number(a.order_no) ?? 0) - (Number(b.order_no) ?? 0)
    );
    barItems = [];
    const groupIdsAdded = new Set();
    tabsByOrder.forEach((tab) => {
      const gk = getGroupKey(tab);
      if (!gk) {
        barItems.push({ type: 'tab', tab });
        return;
      }
      if (groupIdsAdded.has(gk)) return;
      groupIdsAdded.add(gk);
      const groupTabs = visibleTabs
        .filter((t) => getGroupKey(t) === gk)
        .sort((a, b) => (Number(a.order_no) ?? 0) - (Number(b.order_no) ?? 0));
      const label = getGroupLabel(groupTabs[0]) || titleCase(gk);
      barItems.push({ type: 'group', id: `api-${gk}`, key: gk, label, tabs: groupTabs });
    });
  }

  const slotOrderMismatch =
    Array.isArray(slotOrder) &&
    slotOrder.length > 0 &&
    selectedApp?.config?.tabs?.length > 0 &&
    visibleTabs.length > 0 &&
    barItems.length === 0;

  return {
    visibleTabs,
    barItems,
    menuDebug: {
      trace,
      licenseDropDetail,
      unresolvedAppEntries,
      hasAppTabs,
      selectedAppId: selectedApp?.id ?? null,
      selectedAppLabel: selectedApp?.name ?? selectedApp?.app_name ?? null,
      apiTabCount: activeTabs.length,
      slotOrderLength: Array.isArray(slotOrder) ? slotOrder.length : 0,
      slotOrderMismatch,
      enabledPackages,
      allowedTabIdsCount: allowedTabIds.length,
      allowedTabNamesCount: allowedTabNames.length,
    },
  };
}

const Menu = ({ selectedAppId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTabs, setActiveTabs] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [loadingTabs, setLoadingTabs] = useState(true);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [moreHoveredGroupKey, setMoreHoveredGroupKey] = useState(null);

  useEffect(() => {
    fetchActiveTabs();
  }, []);

  useEffect(() => {
    if (!selectedAppId) {
      setSelectedApp(null);
      return;
    }
    const fetchApp = async () => {
      try {
        const appsApi = new DataApi('apps');
        const res = await appsApi.fetchById(selectedAppId);
        if (res?.data?.success && res.data.data) setSelectedApp(res.data.data);
        else setSelectedApp(null);
      } catch (err) {
        console.error('Error fetching selected app for menu:', err);
        setSelectedApp(null);
      }
    };
    fetchApp();
  }, [selectedAppId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (openDropdownId !== null) {
      const handleClickOutside = (event) => {
        // Check if click is outside the dropdown
        const dropdownElement = document.querySelector(`[data-tab-dropdown-id="${openDropdownId}"]`);
        if (dropdownElement && !dropdownElement.contains(event.target)) {
          setOpenDropdownId(null);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openDropdownId]);

  const fetchActiveTabs = async () => {
    try {
      setLoadingTabs(true);
      const tabApi = new DataApi('tabs');
      // Fetch tabs for web platform (use 'platform=mobile' for mobile app)
      const response = await tabApi.fetchAll('is_active=true&platform=web');
      if (tabMenuDebugEnabled() && response?.data?.success) {
        console.log('[Menu/tab-debug] GET /tabs count:', response.data.data?.length);
      }
      if (response?.data?.success) {
        // Sort tabs by order_no (already sorted by backend, but keeping for safety)
        const sortedTabs = response.data.data.sort(
          (a, b) => a.order_no - b.order_no
        );
        setActiveTabs(sortedTabs);
      } else {
        setActiveTabs([]);
      }
    } catch (error) {
      console.error('Error fetching active tabs:', error);
      setActiveTabs([]);
    } finally {
      setLoadingTabs(false);
    }
  };

  const normalizePath = path => {
    if (!path) {
      return '';
    }
    if (path === '/') {
      return '/';
    }
    const trimmed = path.replace(/\/+$/, '');
    return trimmed.toLowerCase();
  };

  const currentPath = normalizePath(location.pathname);

  const doesPathMatch = basePath => {
    if (!basePath) return false;
    if (currentPath === basePath) {
      return true;
    }
    return currentPath.startsWith(`${basePath}/`);
  };

  const isTabActive = tab => {
    if (!tab) return false;

    const tabUrl = normalizePath(tab.url);
    if (tabUrl && doesPathMatch(tabUrl)) {
      return true;
    }

    const objectName = tab.object_name?.toLowerCase();
    if (objectName) {
      const objectPaths = ['/listview', '/view', '/edit'].map(
        prefix => `${prefix}/${objectName}`
      );

      if (objectPaths.some(path => doesPathMatch(path))) {
        return true;
      }
    }

    return false;
  };

  const menuResult = useMemo(
    () =>
      buildMenuTabComputation({
        activeTabs,
        selectedApp,
        enabledPackages: getEnabledPackages(),
        allowedTabIds: getAllowedTabIds(),
        allowedTabNames: getAllowedTabNames(),
        hasWatconnectPackage: hasPackage('watconnect'),
      }),
    [activeTabs, selectedApp]
  );

  const { visibleTabs, barItems, menuDebug } = menuResult;

  const tabDebugLastKeyRef = useRef('');
  useEffect(() => {
    if (!tabMenuDebugEnabled()) return;
    if (loadingTabs) return;

    if (activeTabs.length === 0) {
      const key = 'api-empty';
      if (tabDebugLastKeyRef.current !== key) {
        tabDebugLastKeyRef.current = key;
        console.warn('[Menu/tab-debug] No rows from GET /tabs after load.');
      }
      return;
    }

    if (visibleTabs.length > 0 && !menuDebug.slotOrderMismatch) return;

    const key = `${menuDebug.apiTabCount}:${String(menuDebug.selectedAppId)}:${visibleTabs.length}:${barItems.length}:${menuDebug.slotOrderMismatch}`;
    if (tabDebugLastKeyRef.current === key) return;
    tabDebugLastKeyRef.current = key;

    if (visibleTabs.length === 0) {
      console.warn('[Menu/tab-debug] All tabs filtered out (see trace + licenseDropDetail).', menuDebug);
    }
    if (menuDebug.slotOrderMismatch) {
      console.warn(
        '[Menu/tab-debug] visibleTabs > 0 but slotOrder built an empty bar — check slot id/name vs tenant tab rows.',
        { visibleTabCount: visibleTabs.length, barItemCount: barItems.length, menuDebug }
      );
    }
  }, [loadingTabs, activeTabs.length, visibleTabs.length, barItems.length, menuDebug]);

  if (loadingTabs) {
    return (
      <div className='d-flex align-items-center'>
        <div
          className='spinner-border spinner-border-sm text-light me-2'
          role='status'
        >
          <span className='visually-hidden'>Loading...</span>
        </div>
        <span className='text-light'>Loading...</span>
      </div>
    );
  }

  // Split bar items into visible (first 10) and "More" dropdown; only top-level tabs go to More, groups stay as single items
  const maxVisible = 10;
  const visibleBarItems = barItems.slice(0, maxVisible);
  const moreBarItems = barItems.slice(maxVisible);
  const routeActiveTab = visibleTabs.find((tab) => isTabActive(tab));
  const activeInVisible = routeActiveTab && visibleBarItems.some((item) =>
    item.type === 'tab' ? item.tab.id === routeActiveTab.id : item.tabs.some((t) => t.id === routeActiveTab.id)
  );
  const activeBarItem = routeActiveTab && barItems.find((item) =>
    item.type === 'tab' ? item.tab.id === routeActiveTab.id : item.tabs.some((t) => t.id === routeActiveTab.id)
  );
  const supplementalBarItems = !activeInVisible && activeBarItem ? [activeBarItem] : [];
  const orderedVisibleBarItems =
    supplementalBarItems.length > 0
      ? [...visibleBarItems.filter((item) => item !== activeBarItem), ...supplementalBarItems]
      : visibleBarItems;
  const dropdownBarItems = moreBarItems.filter((item) => item !== activeBarItem);

  const handleCreateRecord = tab => {
    const objectName = tab.object_name?.toLowerCase();
    if (!objectName) return;
    navigate(`/edit/${objectName}`);
  };

  return (
    <>
      {/* Render bar: top-level tabs and group dropdowns in one order (by order_no) */}
      {orderedVisibleBarItems.map((barItem) => {
        if (barItem.type === 'group') {
          const group = barItem;
          const groupActive = group.tabs.some((tab) => isTabActive(tab));
          return (
            <Dropdown key={group.id}>
              <Dropdown.Toggle
                variant="link"

                className="menu-group-toggle"
                style={{
                  color: groupActive ? '#0d47a1' : '#1a1a1a',
                  border: 'none',
                  backgroundColor: 'transparent',
                  borderRadius: 0,
                  margin: '0',
                  padding: '10px 16px',
                  transition: 'all 0.2s ease',
                  fontSize: '14px',
                  fontWeight: groupActive ? '600' : '500',
                  borderBottom: groupActive ? '3px solid #0d47a1' : '3px solid transparent',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#0d47a1';
                  e.currentTarget.style.borderBottom = '3px solid #bbdefb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = groupActive ? '#0d47a1' : '#1a1a1a';
                  e.currentTarget.style.borderBottom = groupActive ? '3px solid #0d47a1' : '3px solid transparent';
                }}
              >
                {group.label} <FaChevronDown size={10} style={{ marginLeft: '4px' }} />
              </Dropdown.Toggle>
              <Dropdown.Menu
                align="end"
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  minWidth: '200px',
                  padding: '8px 0',
                  maxHeight: '400px',
                  overflowY: 'auto',
                }}
              >
                {group.tabs.map((tab) => (
                  <Dropdown.Item
                    key={tab.id}
                    as={NavLink}
                    to={tab.url || '#'}
                    style={{
                      color: isTabActive(tab) ? theme.colors.primary : '#333',
                      fontWeight: isTabActive(tab) ? 'bold' : 'normal',
                      padding: '8px 16px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      backgroundColor: isTabActive(tab) ? '#f8f9fa' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.color = theme.colors.primary;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = isTabActive(tab) ? '#f8f9fa' : 'transparent';
                      e.target.style.color = isTabActive(tab) ? theme.colors.primary : '#333';
                    }}
                  >
                    {tab.label || tab.name?.replace(new RegExp(group.key, 'gi'), '').replace(/_/g, ' ').trim() || tab.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          );
        }
        const tab = barItem.tab;
        const renderTabLink = () => (
          <NavLink
            to={tab.url || '#'}
            className='nav-link menu-nav-link'
            style={({ isActive }) => {
              const active = isActive || isTabActive(tab);
              return {
                color: active ? '#0d47a1' : '#1a1a1a',
                fontWeight: active ? '600' : '500',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: 0,
                margin: '0',
                padding: '10px 16px',
                transition: 'all 0.2s ease',
                borderBottom: active
                  ? '3px solid #0d47a1'
                  : '3px solid transparent',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              };
            }}
            onMouseEnter={e => {
              if (!isTabActive(tab)) {
                e.currentTarget.style.color = '#0d47a1';
                e.currentTarget.style.borderBottom = '3px solid #bbdefb';
              }
            }}
            onMouseLeave={e => {
              const active = isTabActive(tab);
              e.currentTarget.style.color = active ? '#0d47a1' : '#1a1a1a';
              e.currentTarget.style.borderBottom = active
                ? '3px solid #0d47a1'
                : '3px solid transparent';
            }}
          >
            {tab.label}
          </NavLink>
        );

        if (!tab.object_name) {
          return (
            <div
              key={tab.id}
              style={{ display: 'inline-flex', alignItems: 'center' }}
            >
              {renderTabLink()}
            </div>
          );
        }

        return (
          <div
            key={tab.id}
            data-tab-dropdown-id={tab.id}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <div onClick={(e) => e.stopPropagation()}>
              {renderTabLink()}
            </div>
            <Dropdown
              align='end'
              show={openDropdownId === tab.id}
              onToggle={(isOpen) => {
                if (isOpen) {
                  setOpenDropdownId(tab.id);
                } else {
                  setOpenDropdownId(null);
                }
              }}
            >
              <Dropdown.Toggle
                variant='link'
                size='sm'
                className='tab-dropdown-toggle'
                style={{
                  color: '#1a1a1a',
                  border: 'none',
                  backgroundColor: 'transparent',
                  padding: '10px 6px',
                  marginLeft: '-8px',
                  borderBottom: '3px solid transparent',
                }}
                onClick={(e) => {
                  // Stop propagation to prevent tab navigation when clicking chevron
                  e.stopPropagation();
                  e.preventDefault();
                  setOpenDropdownId(openDropdownId === tab.id ? null : tab.id);
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = theme.colors.primary;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = '#1a1a1a';
                }}
              >
                <FaChevronDown size={10} />
              </Dropdown.Toggle>
              <Dropdown.Menu
                style={{
                  minWidth: 'auto',
                  width: 'max-content',
                  zIndex: 10060,
                  padding: '4px 0',
                }}
              >
                <Dropdown.Item
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDropdownId(null);
                    handleCreateRecord(tab);
                  }}
                  className='d-flex align-items-center'
                  style={{
                    padding: '6px 12px',
                    gap: '8px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <FaPlus size={12} style={{ flexShrink: 0 }} />
                  <span>New {tab.label || tab.object_name}</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        );
      })}

      {/* More: remaining bar items (tabs and groups) */}
      {dropdownBarItems.length > 0 && (
        <Dropdown className="menu-more-dropdown" >
          <Dropdown.Toggle
            className="no-caret"
            variant='link'
            style={{
              color: '#1a1a1a',
              border: 'none',
              backgroundColor: 'transparent',
              borderRadius: 0,
              margin: '0',
              padding: '10px 16px',
              transition: 'all 0.2s ease',
              fontSize: '14px',
              fontWeight: '500',
              borderBottom: '3px solid transparent',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
            onMouseEnter={e => {
              e.target.style.color = '#0d47a1';
              e.target.style.borderBottom = '3px solid #bbdefb';
            }}
            onMouseLeave={e => {
              e.target.style.color = '#1a1a1a';
              e.target.style.borderBottom = '3px solid transparent';
            }}
          >
            More ({dropdownBarItems.length}) <FaChevronDown size={10} style={{ marginLeft: '4px' }} />
          </Dropdown.Toggle>

          <Dropdown.Menu
            className="menu-more-dropdown-menu"
            align='end'
            style={{
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              minWidth: '200px',
              padding: '8px 0',
              maxHeight: '400px',
              overflowY: 'auto',
              overflowX: 'visible',
            }}
          >
            {dropdownBarItems.map((barItem) => {
              if (barItem.type === 'tab') {
                const tab = barItem.tab;
                return (
                  <Dropdown.Item
                    key={tab.id}
                    as={NavLink}
                    to={tab.url || '#'}
                    style={{
                      color: isTabActive(tab) ? theme.colors.primary : '#333',
                      fontWeight: isTabActive(tab) ? 'bold' : 'normal',
                      padding: '8px 16px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      backgroundColor: isTabActive(tab) ? '#f8f9fa' : 'transparent',
                    }}
                    onMouseEnter={(e) => { e.target.style.backgroundColor = '#f8f9fa'; e.target.style.color = theme.colors.primary; }}
                    onMouseLeave={(e) => { e.target.style.backgroundColor = isTabActive(tab) ? '#f8f9fa' : 'transparent'; e.target.style.color = isTabActive(tab) ? theme.colors.primary : '#333'; }}
                  >
                    {tab.label}
                  </Dropdown.Item>
                );
              }
              const group = barItem;
              const isHovered = moreHoveredGroupKey === group.key;
              return (
                <div
                  key={`group-${group.key}`}
                  className="menu-more-group-item"
                  onMouseEnter={() => setMoreHoveredGroupKey(group.key)}
                  onMouseLeave={() => setMoreHoveredGroupKey(null)}
                  style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    backgroundColor: isHovered ? '#f8f9fa' : 'transparent',
                    color: '#333',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{group.label}</span>
                  <FaChevronRight size={10} className="ms-2" style={{ flexShrink: 0 }} />
                  {isHovered && (
                    <div
                      className="menu-more-flyout"
                      onMouseEnter={() => setMoreHoveredGroupKey(group.key)}
                      onMouseLeave={() => setMoreHoveredGroupKey(null)}
                      style={{
                        position: 'absolute',
                        left: '100%',
                        top: 0,
                        minWidth: '180px',
                        width: 'max-content',
                        overflow: 'visible',
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        padding: '6px 0',
                        zIndex: 10070,
                        marginLeft: '2px',
                      }}
                    >
                      {group.tabs.map((tab) => (
                        <NavLink
                          key={tab.id}
                          to={tab.url || '#'}
                          className="dropdown-item"
                          style={{
                            display: 'block',
                            padding: '8px 16px',
                            fontSize: '14px',
                            color: isTabActive(tab) ? theme.colors.primary : '#333',
                            fontWeight: isTabActive(tab) ? 'bold' : 'normal',
                            textDecoration: 'none',
                            backgroundColor: 'transparent',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fa'; e.currentTarget.style.color = theme.colors.primary; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = isTabActive(tab) ? theme.colors.primary : '#333'; }}
                        >
                          {tab.label || tab.name}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
      )}
    </>
  );
};

export default Menu;
